import { inject, Injectable } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import de from '@angular/common/locales/de';
import enGB from '@angular/common/locales/en-GB';
import en from '@angular/common/locales/en';
import { TranslocoService } from '@ngneat/transloco';
import { DateFormatOptions, TranslocoLocaleService, ValidDate } from '@ngneat/transloco-locale';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  CHECKIN_CHECKOUT_DATE,
  CHECKIN_CHECKOUT_DATE_US,
  CHECKIN_CHECKOUT_DATE_WITH_YEAR,
  CHECKIN_CHECKOUT_DATE_WITH_YEAR_US,
  DateRangeWeekDay,
  DAY_MONTH_YEAR,
  DAY_MONTH_YEAR_DE,
  DAY_MONTH_YEAR_US,
  DAY_NAME_MONTH_NAME_YEAR,
  DAY_NAME_MONTH_NAME_YEAR_US,
  DAY_OF_WEEK,
  DAY_OF_WEEK_DE,
  DAY_OF_WEEK_LONG,
  DAY_OF_WEEK_LONG_US,
  DAY_OF_WEEK_US,
  ENVIRONMENT_TOKEN,
  EURO_TIME_FORMAT,
  FILE_NAME,
  FILE_NAME_US,
  US_TIME_FORMAT,
} from '@rx-applications/shared/utils';
import { isSameYear } from 'date-fns';
import { getLocalisedDistance } from '@rx-applications/clientsite/shared/internationalization/utils';

const LOCALES = {
  de,
  'en-US': en,
  'en-GB': enGB,
};

@Injectable({ providedIn: 'root' })
export class InternationalizationService {
  environment = inject(ENVIRONMENT_TOKEN);
  #translocoService = inject(TranslocoService);
  #translocoLocaleService = inject(TranslocoLocaleService);

  supportedLanguages: string[] = [];

  readonly #storedLanguageLocalStorageKey = 'rx-lang';

  get isUSLocale(): boolean {
    return this.#translocoService.getActiveLang() === 'en-US';
  }
  get isGermanLocale(): boolean {
    return this.getLocaleId().toLocaleLowerCase() === 'de';
  }

  get firstDayOfWeek(): DateRangeWeekDay {
    return this.isUSLocale ? DateRangeWeekDay.Sunday : DateRangeWeekDay.Monday;
  }

  get datePickerFormat(): string {
    const locale = this.getLocaleId();
    switch (locale) {
      case 'en-US':
        return DAY_OF_WEEK_US;
      case 'de':
        return DAY_OF_WEEK_DE;
      default:
        return DAY_OF_WEEK;
    }
  }

  get fullDateWithDayNameFormat(): string {
    const locale = this.getLocaleId();
    switch (locale) {
      case 'en-US':
        return DAY_NAME_MONTH_NAME_YEAR_US;
      case 'de':
        return DAY_NAME_MONTH_NAME_YEAR;
      default:
        return DAY_NAME_MONTH_NAME_YEAR;
    }
  }

  get dayMonthYear(): string {
    const locale = this.getLocaleId();
    switch (locale) {
      case 'en-US':
        return DAY_MONTH_YEAR_US;
      case 'de':
        return DAY_MONTH_YEAR_DE;
      default:
        return DAY_MONTH_YEAR;
    }
  }

  get fileName(): string {
    const locale = this.getLocaleId();
    switch (locale) {
      case 'en-US':
        return FILE_NAME_US;
      default:
        return FILE_NAME;
    }
  }

  get dayOfWeekLong(): string {
    const locale = this.getLocaleId();
    switch (locale) {
      case 'en-US':
        return DAY_OF_WEEK_LONG_US;
      default:
        return DAY_OF_WEEK_LONG;
    }
  }

  get checkinCheckoutDate(): string {
    const locale = this.getLocaleId();
    switch (locale) {
      case 'en-US':
        return CHECKIN_CHECKOUT_DATE_US;
      default:
        return CHECKIN_CHECKOUT_DATE;
    }
  }
  get checkinCheckoutDateWithYear(): string {
    const locale = this.getLocaleId();
    switch (locale) {
      case 'en-US':
        return CHECKIN_CHECKOUT_DATE_WITH_YEAR_US;
      default:
        return CHECKIN_CHECKOUT_DATE_WITH_YEAR;
    }
  }

  get regionalTimeFormat(): string {
    return this.isUSLocale ? US_TIME_FORMAT : EURO_TIME_FORMAT;
  }

  public getCheckinDateFormat(checkInDate: string, checkOutDate: string): string {
    if (isSameYear(new Date(checkOutDate), new Date(checkInDate)) && isSameYear(new Date(checkInDate), new Date())) {
      return this.checkinCheckoutDate;
    }
    return this.checkinCheckoutDateWithYear;
  }

  /**
   * Initializes i18n for the application.
   * Loads language from local storage if present, or sets default language.
   */
  init(supportedLanguages: string[]) {
    this.supportedLanguages = supportedLanguages;
  }

  /**
   * Sets the current language.
   * Note: The current language is saved as a cookie.
   * If no parameter is specified, the language is loaded from the referrer, cookie or browser,
   * in that order of preference.
   */
  legacySetLanguage(chosenLanguage?: string) {
    const language = this.#legacyGetInitialLanguage(chosenLanguage);
    const supportedLanguage = this.#getSupportedLanguage(language);
    this.#setTranslocoActiveLanguage(supportedLanguage);
  }

  initLanguage(chosenLanguage: string) {
    const language = this.getStoredLanguage() || chosenLanguage || 'en-US';
    const supportedLanguage = this.#getSupportedLanguage(language);
    this.#setTranslocoActiveLanguage(supportedLanguage);
  }

  getStoredLanguage(): string | null {
    return localStorage.getItem(this.#storedLanguageLocalStorageKey);
  }

  setLanguage(chosenLanguage: string) {
    const language = chosenLanguage || this.getStoredLanguage() || 'en-US';
    const supportedLanguage = this.#getSupportedLanguage(language);
    this.#rememberActiveLanguage(supportedLanguage);
    this.#setTranslocoActiveLanguage(supportedLanguage);
  }

  /**
   * @param date (ValidDate) a valid that string, number or Date
   * @param options (DateFormatOptions) a custom date formatting
   *  when no value is passed the output will be dd/mm/yyy or mm-dd-yyyy for the US market
   * @returns a formatted date
   * @example dateFormatting('Mon Apr 25 2022 14:30:00', {dateStyle: 'medium'}) returns 'Apr 25, 2022'
   */
  dateFormatting(
    date: ValidDate,
    options: DateFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' },
  ): string {
    const locale = this.getLocaleId();
    const formattedDate = this.#translocoLocaleService.localizeDate(date, this.getLocaleId(), options);
    return locale === 'en-US' ? formattedDate.replace(/\//g, '-') : formattedDate;
  }

  getLanguage(): Observable<string> {
    return this.#translocoService.langChanges$.pipe(map(this.#transformLocaleToLanguage));
  }

  getLocale(): Observable<string> {
    return this.#translocoService.langChanges$;
  }

  getLocaleId(): string {
    return this.#translocoService.getActiveLang() || 'en-GB';
  }

  legacyGetStoredLanguage(): string | null {
    const activeLang = this.#translocoService.getActiveLang();
    return activeLang == 'en' ? null : activeLang;
  }

  getActiveLanguage(): string | null {
    const activeLang = this.#translocoService.getActiveLang();
    return activeLang == 'en' ? null : activeLang;
  }

  getLocalisedDistance(distance: number, useDefaultLocale: boolean, overrideUSRegionalSettings?: boolean): string {
    if (!useDefaultLocale) {
      return getLocalisedDistance(distance, !!overrideUSRegionalSettings);
    }

    const locale = this.getLocaleId();
    return locale === 'en-US' ? `${(distance * 0.621371).toFixed(1)}mi` : `${distance}km`;
  }

  #transformLocaleToLanguage(locale: string) {
    return locale?.split('-')[0];
  }

  #legacyGetInitialLanguage(chosenLanguage?: string): string {
    const browserLanguage = this.#getBrowserLanguage();
    const storedLanguage = this.legacyGetStoredLanguage();
    const referrerLanguage = this.#getReferrerLanguage();

    return chosenLanguage || referrerLanguage || storedLanguage || browserLanguage;
  }

  #getSupportedLanguage(language: string): string {
    const languagePart: string = language.split('-')[0];
    const isLocaleSupported: boolean = this.supportedLanguages.some((supportedLanguage) => supportedLanguage === language);

    if ((!isLocaleSupported && languagePart === 'de') || language === 'de') {
      return 'de';
    }
    return isLocaleSupported ? language : 'en-US';
  }

  #rememberActiveLanguage(language: string): void {
    localStorage.setItem(this.#storedLanguageLocalStorageKey, language);
  }

  #setTranslocoActiveLanguage(language: string): void {
    this.#translocoService.setActiveLang(language);
    registerLocaleData(LOCALES[language], language);
  }

  #getBrowserLanguage(): string {
    return navigator.language;
  }

  /**
   * @returns Gets supported language that matches language in url
   */
  #getReferrerLanguage(): string | undefined {
    const { referrer } = document;
    if (!referrer) {
      return undefined;
    }
    const urlKeys = referrer.split('/');
    return this.supportedLanguages.find((supportedLanguage) => urlKeys.includes(supportedLanguage));
  }

  changeUSLocaleToGB(locale: string) {
    return locale === 'en-US' ? 'en-GB' : locale;
  }
}
